import type { AppState } from '../types';
import { sameCustomerContent } from './reconcile';
import { captureLegacyPlans } from './plans';
import {
  inferAreaTypeFromName,
  initialState,
  initialGuestState,
  newEmptyState,
  validateGeometry,
} from './domain';

export const STORAGE_KEY = 'plotwise.state.v1';
export const GUEST_STORAGE_KEY = 'plotwise.guest.state.v1';
const storageBaselines = new Map<string, string | null>();
export const workspaceStorageKey = (scope?: string): string =>
  scope ? `${STORAGE_KEY}.user.${encodeURIComponent(scope)}` : STORAGE_KEY;
const object = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const string = (v: unknown): v is string => typeof v === 'string';
const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const date = (v: unknown) => string(v) && !Number.isNaN(Date.parse(v));
const optionalString = (v: unknown) => v === undefined || string(v);
const unique = (items: Record<string, unknown>[]) =>
  new Set(items.map((i) => i.id)).size === items.length;

export function validateState(value: unknown): value is AppState {
  if (
    !object(value) ||
    value.version !== 1 ||
    !object(value.profile) ||
    !['name', 'email', 'company'].every((k) =>
      string(value.profile && (value.profile as Record<string, unknown>)[k]),
    )
  )
    return false;
  if (!string(value.selectedEstateId) || !string(value.selectedPlanId)) return false;
  const keys = [
    'estates',
    'fields',
    'plans',
    'activities',
    'tasks',
    'attachments',
    'products',
  ] as const;
  for (const key of keys) {
    const collection = value[key];
    if (
      !Array.isArray(collection) ||
      !collection.every((item) => object(item) && string(item.id) && item.id.length > 0) ||
      !unique(collection)
    )
      return false;
  }
  const estates = value.estates as Record<string, unknown>[];
  const estateExists = (id: unknown) => estates.some((e) => e.id === id);
  if (
    !estates.every(
      (e) =>
        string(e.name) &&
        string(e.address) &&
        date(e.createdAt) &&
        Array.isArray(e.center) &&
        e.center.length === 2 &&
        e.center.every(finite) &&
        Math.abs(e.center[0]) <= 180 &&
        Math.abs(e.center[1]) <= 90,
    )
  )
    return false;
  const fields = value.fields as Record<string, unknown>[];
  if (
    !fields.every(
      (f) =>
        estateExists(f.estateId) &&
        string(f.name) &&
        string(f.notes) &&
        (f.kind === undefined || f.kind === 'field' || f.kind === 'zone') &&
        (f.areaType === undefined || string(f.areaType)) &&
        (f.advisorOnly === undefined || typeof f.advisorOnly === 'boolean') &&
        date(f.updatedAt) &&
        object(f.geometry) &&
        validateGeometry(f.geometry as AppState['fields'][number]['geometry']) === null,
    )
  )
    return false;
  const fieldExists = (id: unknown, estateId: unknown) =>
    id === undefined || fields.some((f) => f.id === id && f.estateId === estateId);
  const products = value.products as Record<string, unknown>[];
  if (
    !products.every(
      (p) =>
        ['name', 'description', 'color', 'url'].every((k) => string(p[k])) &&
        ['rateKgHa', 'packKg', 'priceDkk'].every((k) => finite(p[k]) && (p[k] as number) > 0) &&
        typeof p.active === 'boolean' &&
        typeof p.verified === 'boolean' &&
        (p.unit === undefined || ['kg', 'korn', 'kerner'].includes(String(p.unit))) &&
        optionalString(p.variantId) &&
        optionalString(p.source) &&
        (p.checkedAt === undefined || date(p.checkedAt)),
    )
  )
    return false;
  const plans = value.plans as Record<string, unknown>[];
  for (const p of plans) {
    if (
      !estateExists(p.estateId) ||
      !string(p.name) ||
      !string(p.notes) ||
      !date(p.updatedAt) ||
      !finite(p.year) ||
      !Number.isInteger(p.year) ||
      p.year < 1900 ||
      p.year > 2200 ||
      !['draft', 'ready'].includes(String(p.status)) ||
      !object(p.selections)
    )
      return false;
    let planFields = fields;
    let planProducts = products;
    if (p.snapshot !== undefined) {
      const snapshot = p.snapshot;
      if (
        !object(snapshot) ||
        !date(snapshot.capturedAt) ||
        !Array.isArray(snapshot.fields) ||
        !Array.isArray(snapshot.products)
      )
        return false;
      if (
        !snapshot.fields.every(
          (f) =>
            object(f) &&
            string(f.id) &&
            f.estateId === p.estateId &&
            string(f.name) &&
            string(f.notes) &&
            (f.kind === undefined || f.kind === 'field' || f.kind === 'zone') &&
            (f.areaType === undefined || string(f.areaType)) &&
            date(f.updatedAt) &&
            object(f.geometry) &&
            validateGeometry(f.geometry as AppState['fields'][number]['geometry']) === null,
        ) ||
        !unique(snapshot.fields)
      )
        return false;
      if (
        !snapshot.products.every(
          (product) =>
            object(product) &&
            string(product.id) &&
            ['name', 'description', 'color', 'url'].every((k) => string(product[k])) &&
            ['rateKgHa', 'packKg', 'priceDkk'].every(
              (k) => finite(product[k]) && (product[k] as number) > 0,
            ) &&
            typeof product.active === 'boolean' &&
            typeof product.verified === 'boolean' &&
            (product.unit === undefined || ['kg', 'korn', 'kerner'].includes(String(product.unit))),
        ) ||
        !unique(snapshot.products)
      )
        return false;
      planFields = snapshot.fields;
      if (p.status === 'ready') planProducts = snapshot.products;
    }
    if (
      !Object.entries(p.selections).every(
        ([fieldId, productId]) =>
          planFields.some((f) => f.id === fieldId && f.estateId === p.estateId) &&
          string(productId) &&
          planProducts.some((product) => product.id === productId),
      )
    )
      return false;
  }
  if (
    !(value.activities as Record<string, unknown>[]).every(
      (a) =>
        estateExists(a.estateId) &&
        fieldExists(a.fieldId, a.estateId) &&
        optionalString(a.fieldId) &&
        ['Såning', 'Fodring', 'Slåning', 'Øvrig pleje'].includes(String(a.kind)) &&
        date(a.date) &&
        ['planned', 'done'].includes(String(a.status)) &&
        string(a.description) &&
        date(a.createdAt) &&
        (a.quantity === undefined || (finite(a.quantity) && a.quantity >= 0)) &&
        optionalString(a.unit),
    )
  )
    return false;
  if (
    !(value.attachments as Record<string, unknown>[]).every(
      (a) =>
        estateExists(a.estateId) &&
        fieldExists(a.fieldId, a.estateId) &&
        optionalString(a.fieldId) &&
        string(a.name) &&
        string(a.mime) &&
        finite(a.size) &&
        a.size >= 0 &&
        date(a.createdAt) &&
        (a.storage === undefined || ['local', 'cloud'].includes(String(a.storage))),
    )
  )
    return false;
  if (value.activityHistory !== undefined) {
    if (
      !Array.isArray(value.activityHistory) ||
      !unique(value.activityHistory) ||
      !value.activityHistory.every(
        (event) =>
          object(event) &&
          string(event.id) &&
          string(event.activityId) &&
          estateExists(event.estateId) &&
          ['created', 'status_changed', 'deleted', 'updated'].includes(String(event.action)) &&
          date(event.at) &&
          string(event.actor) &&
          (event.source === undefined || ['local', 'cloud'].includes(String(event.source))) &&
          [event.before, event.after].every(
            (item) =>
              item === undefined ||
              (object(item) &&
                item.estateId === event.estateId &&
                string(item.id) &&
                date(item.date) &&
                string(item.description) &&
                ['planned', 'done'].includes(String(item.status))),
          ),
      )
    )
      return false;
    if (
      !(value.tasks as Record<string, unknown>[]).every(
        (t) =>
          estateExists(t.estateId) &&
          string(t.title) &&
          string(t.notes) &&
          date(t.dueDate) &&
          date(t.createdAt) &&
          ['open', 'done'].includes(String(t.status)) &&
          (t.completedAt === undefined || date(t.completedAt)),
      )
    )
      return false;
  }
  if (value.selectedEstateId !== '' && !estateExists(value.selectedEstateId)) return false;
  if (
    value.selectedPlanId !== '' &&
    !plans.some((p) => p.id === value.selectedPlanId && p.estateId === value.selectedEstateId)
  )
    return false;
  return true;
}

/**
 * Tilpas en lagret tilstand i stedet for at afvise den.
 *
 * Maalt 16/10 kl. 07:39: Mikkel fik "Gemte data kunne ikke laeses" paa en konto med
 * rigtige data, og automatisk lagring stoppede. AArsagen er at samlinger og felter er
 * blevet tilfoejet hen ad vejen — fx `tasks` og `attachments`, og `notes` paa en mark —
 * og en tilstand der er skrevet foer de fandtes (fx fra skyen, fra en anden enhed eller
 * fra en tidligere version) bliver afvist af validateState. Brugeren staar da med en
 * fejlbesked og sine data laast inde.
 *
 * Vi udfylder derfor det der mangler med tomme vaerdier, saa dataene kan laeses. Det er
 * ikke det samme som at slække paa valideringen: alt hvad der ER der, valideres stadig,
 * og en fil med forkerte vaerdier afvises fortsat. Vi tilgiver kun det der MANGLER.
 */
function normalizeState(value: unknown): unknown {
  if (!object(value)) return value;
  const v = value as Record<string, unknown>;
  for (const key of [
    'estates',
    'fields',
    'plans',
    'activities',
    'tasks',
    'attachments',
    'products',
  ]) {
    if (!Array.isArray(v[key])) v[key] = [];
  }
  if (Array.isArray(v.fields)) {
    v.fields = v.fields.map((f) => {
      if (!object(f)) return f;
      const rawNotes = string(f.notes) ? f.notes : '';
      const isAdvisorOnly =
        rawNotes.includes('[RÅDGIVER_KLADDE]') || (f as any).advisorOnly === true;
      const notes = rawNotes.replace(/\[RÅDGIVER_KLADDE\]\s*/g, '');
      const name = string(f.name) ? f.name : '';
      let areaType = (f as any).areaType;
      let kind = (f as any).kind;
      if (!areaType) {
        const match = notes.match(/^\[OMRÅDE(?::([a-z_]+))?\]\s*/);
        if (match) {
          areaType = match[1] || 'jagtomraade';
          if (!kind) kind = areaType === 'jagtomraade' ? 'zone' : 'field';
        } else if (!notes.startsWith('[POI:')) {
          const inferred = inferAreaTypeFromName(name);
          if (inferred) {
            areaType = inferred;
            if (!kind) kind = inferred === 'jagtomraade' ? 'zone' : 'field';
          }
        }
      }
      if (
        areaType === 'remise' &&
        (inferAreaTypeFromName(name) === 'laehegn' || notes.includes('Plantet læhegn'))
      ) {
        areaType = 'laehegn';
        kind = 'field';
      }
      return {
        ...f,
        notes,
        ...(isAdvisorOnly ? { advisorOnly: true } : {}),
        ...(areaType ? { areaType } : {}),
        ...(kind ? { kind } : {}),
      };
    });
  }
  if (object(v.profile)) {
    const profile = v.profile as Record<string, unknown>;
    for (const k of ['name', 'email', 'company']) if (!string(profile[k])) profile[k] = '';
  }
  if (!string(v.selectedEstateId)) v.selectedEstateId = '';
  if (!string(v.selectedPlanId)) v.selectedPlanId = '';
  return v;
}

export function importState(raw: string): AppState {
  const value: unknown = normalizeState(JSON.parse(raw));
  if (!validateState(value)) throw new Error('Filen har ugyldige eller ukendte Plotwise-data.');
  return captureLegacyPlans(value);
}

export function exportState(state: AppState): string {
  if (!validateState(state))
    throw new Error('Planen indeholder ugyldige data og kan ikke eksporteres.');
  return JSON.stringify(state, null, 2);
}

export function loadState(scope?: string): { state: AppState; error: string | null } {
  const key = workspaceStorageKey(scope);
  storageBaselines.delete(key);
  try {
    const raw = localStorage.getItem(key);
    const state = raw === null ? (scope ? newEmptyState() : initialState()) : importState(raw);
    storageBaselines.set(key, raw);
    return { state, error: null };
  } catch {
    return {
      state: scope ? newEmptyState() : initialState(),
      error:
        'Gemte data kunne ikke læses. Originalen er bevaret; automatisk gemning er stoppet. Eksportér eller ryd browserdata bevidst før du fortsætter.',
    };
  }
}

export function loadGuestState(): { state: AppState; error: string | null } {
  const key = workspaceStorageKey();
  storageBaselines.delete(key);
  try {
    const existing = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
    const rawGuest =
      typeof localStorage !== 'undefined' ? localStorage.getItem(GUEST_STORAGE_KEY) : null;
    const state = rawGuest === null ? initialGuestState() : importState(rawGuest);
    if (
      state.estates.length === 1 &&
      state.estates[0].center?.[0] === 11.0 &&
      state.estates[0].center?.[1] === 56.0 &&
      (!state.estates[0].address || !state.estates[0].address.trim())
    ) {
      state.estates[0].center = [11.82, 55.4];
    }
    storageBaselines.set(key, existing);
    return { state, error: null };
  } catch {
    const fallback = initialGuestState();
    const existing = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
    storageBaselines.set(key, existing);
    return {
      state: fallback,
      error: 'Gæstedata kunne ikke læses. Originalen er bevaret i browseren.',
    };
  }
}

export function saveGuestState(
  state: AppState,
): { ok: true; error: null } | { ok: false; error: string; kind: StorageErrorKind } {
  if (!validateState(state))
    return {
      ok: false,
      error: 'Planen indeholder ugyldige data og kan ikke gemmes i browseren.',
      kind: 'validation',
    };
  try {
    if (typeof localStorage !== 'undefined') {
      const serialized = exportState(state);
      localStorage.setItem(GUEST_STORAGE_KEY, serialized);
    }
    return { ok: true, error: null };
  } catch {
    return {
      ok: false,
      error: 'Browserens lagerplads er fuld eller utilgængelig. Planen kunne ikke gemmes lokalt.',
      kind: 'unavailable',
    };
  }
}

export function clearGuestState(): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(GUEST_STORAGE_KEY);
      localStorage.removeItem('plotwise.guest.active');
    }
  } catch {
    // Ignore storage errors
  }
}

export function hasGuestPlan(): boolean {
  try {
    if (typeof localStorage === 'undefined') return false;
    const raw = localStorage.getItem(GUEST_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return (
      Array.isArray(parsed?.fields) &&
      parsed.fields.some(
        (f: any) =>
          f &&
          typeof f.id === 'string' &&
          !f.id.startsWith('example-') &&
          (!f.estateId || !String(f.estateId).startsWith('example-')),
      )
    );
  } catch {
    return false;
  }
}

export type StorageErrorKind = 'validation' | 'conflict' | 'corrupt' | 'unloaded' | 'unavailable';
export function saveState(
  state: AppState,
  scope?: string,
): { ok: true; error: null } | { ok: false; error: string; kind: StorageErrorKind } {
  const key = workspaceStorageKey(scope);
  let kind: StorageErrorKind = 'unavailable';
  try {
    const existing = localStorage.getItem(key);
    if (existing !== null) {
      kind = 'corrupt';
      importState(existing);
    }
    if (!storageBaselines.has(key)) {
      kind = 'unloaded';
      throw new Error(
        'Genindlæs de gemte data før du gemmer. Eksportér først arbejdet i denne fane.',
      );
    }
    if (existing !== storageBaselines.get(key)) {
      kind = 'conflict';
      throw new Error(
        'Planen er ændret i en anden fane. Eksportér først arbejdet i denne fane, og genindlæs derefter siden. Den anden fanes ændringer er bevaret.',
      );
    }
    kind = 'validation';
    const serialized = exportState(state);
    kind = 'unavailable';
    try {
      localStorage.setItem(key, serialized);
    } catch {
      freeLocalStorageSpace(scope);
      localStorage.setItem(key, serialized);
    }
    storageBaselines.set(key, serialized);
    return { ok: true, error: null };
  } catch (error) {
    return {
      ok: false,
      kind,
      error: `Planen blev ikke gemt. ${error instanceof Error ? error.message : 'Browserlageret er utilgængeligt eller fuldt.'}`,
    };
  }
}

export function loadCloudBaseline(scope: string): AppState | null {
  try {
    const raw = localStorage.getItem(`${workspaceStorageKey(scope)}.cloud-clean`);
    return raw ? importState(raw) : null;
  } catch {
    return null;
  }
}
export function hasUnsyncedState(scope: string): boolean {
  try {
    const raw = localStorage.getItem(workspaceStorageKey(scope));
    if (!raw) return false;
    const base = loadCloudBaseline(scope);
    const state = importState(raw);
    if (!base) {
      if (sameCustomerContent(state, initialState())) return false;
      if (
        !state.estates.length &&
        !state.fields.length &&
        !state.plans.length &&
        !state.activities.length &&
        !state.tasks.length &&
        !state.attachments.length &&
        !state.profile.name &&
        !state.profile.company
      )
        return false;
      return true;
    }
    return !sameCustomerContent(state, base);
  } catch {
    return false;
  }
}
export const MAX_RECOVERY_SNAPSHOTS = 10;

/**
 * Frigør plads i localStorage progressivt ved kvoteoverskridelse (QuotaExceededError).
 * 1. Renser ældre recovery-snapshots for det aktuelle scope via FIFO (bevarer de nyeste).
 * 2. Renser eventuelle forældede/herreløse .recovery.-snapshots på tværs af andre scopes.
 * 3. Renser midlertidige caches (vejr, integration-logs, sensor-data).
 */
export function freeLocalStorageSpace(scope?: string): boolean {
  if (typeof localStorage === 'undefined') return false;
  let freed = false;

  // 1. Trimming af recovery snapshots i det aktuelle scope via FIFO (bevar højst de 2 nyeste)
  if (scope) {
    const baseKey = workspaceStorageKey(scope);
    const fifoKey = `${baseKey}.recovery_fifo`;
    try {
      const raw = localStorage.getItem(fifoKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          const fifoList = parsed.filter((k) => typeof k === 'string');
          while (fifoList.length > 2) {
            const oldest = fifoList.shift();
            if (oldest) {
              try {
                localStorage.removeItem(oldest);
                freed = true;
              } catch {}
            }
          }
          try {
            localStorage.setItem(fifoKey, JSON.stringify(fifoList));
          } catch {}
        }
      }
    } catch {}
  }

  // 2. Trimming af alle andre herreløse .recovery.-snapshots
  try {
    const orphanedKeys: string[] = [];
    const scopePrefix = scope ? `${workspaceStorageKey(scope)}.recovery.` : null;
    const count = typeof localStorage.length === 'number' ? localStorage.length : 0;
    for (let i = 0; i < count; i++) {
      const k = typeof localStorage.key === 'function' ? localStorage.key(i) : null;
      if (k && k.includes('.recovery.') && (!scopePrefix || !k.startsWith(scopePrefix))) {
        orphanedKeys.push(k);
      }
    }
    for (const k of orphanedKeys) {
      try {
        localStorage.removeItem(k);
        freed = true;
      } catch {}
    }
  } catch {}

  // 3. Trimming af midlertidige caches (vejrdata, sensorlogs, sensorcaches)
  try {
    const cachePrefixes = ['plotwise_weather_', 'plotwise_poi_', 'plotwise_integrations_'];
    const cacheKeys: string[] = [];
    const count = typeof localStorage.length === 'number' ? localStorage.length : 0;
    for (let i = 0; i < count; i++) {
      const k = typeof localStorage.key === 'function' ? localStorage.key(i) : null;
      if (k && cachePrefixes.some((p) => k.startsWith(p))) {
        cacheKeys.push(k);
      }
    }
    for (const k of cacheKeys) {
      try {
        localStorage.removeItem(k);
        freed = true;
      } catch {}
    }
  } catch {}

  return freed;
}

export function preserveRecoveryState(state: AppState, scope: string): boolean {
  try {
    const baseKey = workspaceStorageKey(scope);
    const prefix = `${baseKey}.recovery.`;
    const fifoKey = `${baseKey}.recovery_fifo`;

    // 1. Læs eksisterende FIFO-liste
    let fifoList: string[] = [];
    try {
      const raw = localStorage.getItem(fifoKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          fifoList = parsed.filter((k) => typeof k === 'string');
        }
      }
    } catch {
      fifoList = [];
    }

    // 2. Opdag eventuelle eksisterende recovery-nøgler i localStorage
    const existingKeys = new Set<string>();
    if (typeof localStorage.length === 'number' && typeof localStorage.key === 'function') {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(prefix)) {
          existingKeys.add(k);
        }
      }
    }
    try {
      for (const k of Object.keys(localStorage)) {
        if (k.startsWith(prefix)) {
          existingKeys.add(k);
        }
      }
    } catch {
      // ignore
    }

    // Synkronisér med faktiske nøgler hvis scanning fandt noget
    if (existingKeys.size > 0) {
      fifoList = fifoList.filter((k) => existingKeys.has(k));
      for (const k of existingKeys) {
        if (!fifoList.includes(k)) {
          fifoList.push(k);
        }
      }
    } else if (fifoList.length > 0) {
      // Hvis scanning ikke er mulig, verificer med getItem
      fifoList = fifoList.filter((k) => localStorage.getItem(k) !== null);
    }

    // 3. FIFO pruning: Hvis der er MAX_RECOVERY_SNAPSHOTS eller flere, slet de ældste
    while (fifoList.length >= MAX_RECOVERY_SNAPSHOTS) {
      const oldest = fifoList.shift();
      if (oldest) {
        try {
          if (typeof localStorage.removeItem === 'function') {
            localStorage.removeItem(oldest);
          } else {
            localStorage.setItem(oldest, '');
          }
        } catch {
          // ignore removal errors
        }
      }
    }

    // 4. Gem det nye snapshot med progressiv oprydning ved kvoteoverskridelse
    const serialized = exportState(state);
    const newKey = `${prefix}${crypto.randomUUID()}`;

    let written = false;
    try {
      localStorage.setItem(newKey, serialized);
      written = true;
    } catch {
      // Lagerkvote overskredet (QuotaExceededError).
      // Trin A: Fjern ældre snapshots fra det aktuelle scope et efter et, indtil der er plads
      while (fifoList.length > 0 && !written) {
        const oldest = fifoList.shift();
        if (oldest) {
          try {
            localStorage.removeItem(oldest);
          } catch {}
          try {
            localStorage.setItem(newKey, serialized);
            written = true;
          } catch {}
        }
      }

      // Trin B: Hvis stadig fuld, frigør herreløse recovery-kopier og midlertidige caches
      if (!written) {
        freeLocalStorageSpace(scope);
        try {
          localStorage.setItem(newKey, serialized);
          written = true;
        } catch {}
      }
    }

    if (!written) {
      return false;
    }

    fifoList.push(newKey);

    // 5. Gem opdateret FIFO-liste
    try {
      localStorage.setItem(fifoKey, JSON.stringify(fifoList));
    } catch {
      // Ignorer fejl ved lagring af FIFO-indeks hvis kvoten er overskredet
    }

    return true;
  } catch {
    return false;
  }
}
export function acknowledgeStoredState(state: AppState, scope: string): boolean {
  try {
    const raw = exportState(state);
    const key = `${workspaceStorageKey(scope)}.cloud-clean`;
    try {
      localStorage.setItem(key, raw);
      return true;
    } catch {
      freeLocalStorageSpace(scope);
      try {
        localStorage.setItem(key, raw);
        return true;
      } catch {
        return false;
      }
    }
  } catch {
    return false;
  }
}

function openAttachments(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('Browseren understøtter ikke lokal fillagring.'));
      return;
    }
    const request = indexedDB.open('plotwise.attachments.v1', 1);
    request.onupgradeneeded = () => request.result.createObjectStore('files');
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Fillageret kunne ikke åbnes.'));
    request.onblocked = () => reject(new Error('Fillageret er blokeret af en anden fane.'));
  });
}

async function attachmentOperation<T>(
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openAttachments();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('files', mode);
    const request = operation(transaction.objectStore('files'));
    let result: T;
    request.onsuccess = () => {
      result = request.result;
    };
    transaction.oncomplete = () => {
      db.close();
      resolve(result);
    };
    transaction.onerror = () => {
      db.close();
      reject(transaction.error ?? new Error('Filen kunne ikke gemmes eller læses.'));
    };
    transaction.onabort = () => {
      db.close();
      reject(transaction.error ?? new Error('Filhandlingen blev afbrudt.'));
    };
  });
}

export async function putAttachment(attachmentId: string, file: Blob): Promise<void> {
  if (!['application/pdf', 'image/jpeg', 'image/png', 'image/webp'].includes(file.type))
    throw new Error('Kun PDF, JPEG, PNG og WebP er tilladt.');
  if (file.size > 20 * 1024 * 1024) throw new Error('Filen må højst fylde 20 MB.');
  await attachmentOperation('readwrite', (store) => store.put(file, attachmentId));
}
export const getAttachment = (attachmentId: string): Promise<Blob | undefined> =>
  attachmentOperation('readonly', (store) => store.get(attachmentId));
export async function deleteAttachment(attachmentId: string): Promise<void> {
  await attachmentOperation('readwrite', (store) => store.delete(attachmentId));
}
